import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  Star,
  Users,
  TrendingUp,
  Award,
  ChevronLeft,
  ChevronRight,
  Building2,
  ShoppingCart,
  Heart,
  Zap
} from "lucide-react";
import { Link } from "react-router-dom";

const ClientsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const cardsPerView = 3;
  const totalSlides = Math.ceil(testimonials.length / cardsPerView);

  // Auto-play carousel for testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) =>
        prevIndex === totalSlides - 1 ? 0 : prevIndex + 1
      );
    }, 4000);

    return () => clearInterval(interval);
  }, [totalSlides]);

  const goToPrevious = () => {
    setCurrentIndex(currentIndex === 0 ? totalSlides - 1 : currentIndex - 1);
  };

  const goToNext = () => {
    setCurrentIndex(currentIndex === totalSlides - 1 ? 0 : currentIndex + 1);
  };

  const getCurrentTestimonials = () => {
    const startIndex = currentIndex * cardsPerView;
    return testimonials.slice(startIndex, startIndex + cardsPerView);
  };

  return (
    <section className="relative py-6 overflow-hidden bg-gradient-to-br from-primary/15 via-accent/10 to-primary/20">      {/* Background Effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-primary/10 rounded-full filter blur-3xl opacity-30 animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-accent/10 rounded-full filter blur-3xl opacity-30 animate-pulse delay-1000"></div>
      </div>

      <div className="container-page relative z-10">
        {/* Header */}
        <div className="text-center mb-12 space-y-4">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full text-sm font-medium text-primary">
            <Award className="w-4 h-4" />
            <span>Clientes que Confiam em Nós</span>
          </div>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight">
            <span className="block">Transformamos</span>
            <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              Ideias em Sucesso
            </span>
          </h2>

          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto">
            Mais de <strong className="text-primary">200+ empresas</strong> já escolheram nossa expertise para
            <strong className="text-primary"> revolucionar</strong> seus negócios digitais.
          </p>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="text-center p-6 rounded-xl bg-card border hover:shadow-lg hover:scale-105 transition-all duration-300"
            >
              <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                <stat.icon className="w-6 h-6 text-primary" />
              </div>
              <div className="text-2xl md:text-3xl font-bold text-primary mb-2">
                {stat.value}
              </div>
              <div className="text-sm font-medium text-muted-foreground">
                {stat.label}
              </div>
            </div>
          ))}
        </div>

        {/* Clients Carousel */}
        <div className="mb-16">
          <div className="text-center mb-6">
            <h3 className="text-3xl font-bold">
              <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                Nossos Clientes
              </span>
            </h3>
          </div>

          {/* Animated Logo Grid */}
          <div className="relative overflow-hidden rounded-2xl border bg-card/50 p-8">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
              {clients.map((client, index) => (
                <div
                  key={index}
                  className="group flex flex-col items-center justify-center p-4 rounded-xl hover:bg-accent/5 transition-all duration-300 hover:scale-110"
                >
                  <div className="w-16 h-16 mb-3 rounded-xl bg-gradient-to-br from-primary/10 to-accent/10 flex items-center justify-center group-hover:from-primary/20 group-hover:to-accent/20 transition-all duration-300">
                    <client.icon className="w-8 h-8 text-primary" />
                  </div>
                  <div className="text-center">
                    <div className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">
                      {client.name}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {client.category}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Testimonials Carousel */}
        <div className="mb-16">
          <h3 className="text-3xl font-bold text-center mb-8">
            <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              O que nossos clientes dizem
            </span>
          </h3>

        <div className="max-w-7xl mx-auto">
          <div className="relative">
            {/* Cards Grid */}
            <div className="grid md:grid-cols-3 gap-6 transition-all duration-500">
              {getCurrentTestimonials().map((testimonial) => (
                <div key={testimonial.id} className="group">
                  <div className="bg-background/90 backdrop-blur-sm border border-border/50 hover:border-primary/40 rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 h-full flex flex-col">

                    {/* Stars */}
                    <div className="flex gap-1 mb-4">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 text-yellow-500 fill-current" />
                      ))}
                    </div>

                    {/* Quote */}
                    <blockquote className="text-sm md:text-base text-foreground leading-relaxed mb-6 italic flex-1">
                      "{testimonial.content}"
                    </blockquote>

                    {/* Author */}
                    <div className="flex items-center gap-3">
                      <img
                        src={testimonial.avatar}
                        alt={testimonial.name}
                        className="w-12 h-12 rounded-full border-2 border-primary/20"
                      />
                      <div>
                        <div className="font-bold text-foreground text-sm">
                          {testimonial.name}
                        </div>
                        <div className="text-muted-foreground text-xs">
                          {testimonial.position} • {testimonial.company}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={goToPrevious}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-12 h-12 bg-primary/20 hover:bg-primary/40 rounded-full flex items-center justify-center transition-colors duration-300 group shadow-lg"
            >
              <ChevronLeft className="w-6 h-6 text-primary group-hover:text-white" />
            </button>

            <button
              onClick={goToNext}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-12 h-12 bg-primary/20 hover:bg-primary/40 rounded-full flex items-center justify-center transition-colors duration-300 group shadow-lg"
            >
              <ChevronRight className="w-6 h-6 text-primary group-hover:text-white" />
            </button>
          </div>

          {/* Dots Indicator */}
          <div className="flex justify-center gap-3 mt-8">
            {Array.from({ length: totalSlides }, (_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${index === currentIndex
                  ? 'bg-primary scale-125'
                  : 'bg-primary/30 hover:bg-primary/60'
                  }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Success Stories */}
      <div className="grid md:grid-cols-3 gap-6 mb-16">
        {successStories.map((story, index) => (
          <div
            key={index}
            className="relative p-6 rounded-2xl border bg-gradient-to-br from-card to-card/50 hover:shadow-xl hover:scale-105 transition-all duration-300 group overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

            <div className="relative z-10 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                  <story.icon className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <div className="font-semibold text-foreground">{story.client}</div>
                  <div className="text-xs text-muted-foreground">{story.industry}</div>
                </div>
              </div>

              <p className="text-sm text-muted-foreground leading-relaxed">
                {story.challenge}
              </p>

              <div className="space-y-2">
                <div className="text-sm font-medium text-foreground">Resultados:</div>
                <div className="flex flex-wrap gap-2">
                  {story.results.map((result, idx) => (
                    <span key={idx} className="text-xs bg-primary/10 px-2 py-1 rounded-full text-foreground font-medium">
                      {result}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* CTA Section */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-primary/10 via-background to-accent/10 border p-8 md:p-12">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-primary/10 rounded-full filter blur-3xl opacity-50"></div>

        <div className="relative z-10 text-center space-y-6">
          <div className="space-y-4">
            <h3 className="text-2xl md:text-3xl font-bold text-foreground">
              Pronto para ser o
              <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent"> próximo case de sucesso?</span>
            </h3>

            <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Junte-se a mais de 200+ empresas que já transformaram seus negócios conosco.
              Vamos criar algo incrível juntos!
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button
              size="lg"
              className="text-lg px-8 py-6 bg-primary hover:bg-primary/90 hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
              asChild
            >
              <Link to="/contato">
                Iniciar Meu Projeto
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 pt-6 border-t border-border/50">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Star className="w-4 h-4 text-yellow-500" />
              <span>4.9/5 satisfação</span>
            </div>
            <div className="hidden sm:block w-px h-4 bg-border"></div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Users className="w-4 h-4 text-primary" />
              <span>200+ clientes ativos</span>
            </div>
            <div className="hidden sm:block w-px h-4 bg-border"></div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Award className="w-4 h-4 text-primary" />
              <span>5+ anos de mercado</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    </section >
  );
};

const stats = [
  { value: "200+", label: "Clientes Ativos", icon: Users },
  { value: "500+", label: "Projetos Entregues", icon: Award },
  { value: "98%", label: "Taxa de Satisfação", icon: Star },
  { value: "24h", label: "Suporte Técnico", icon: Zap }
];

const clients = [
  { name: "TechCorp", category: "Tecnologia", icon: Building2 },
  { name: "HealthPlus", category: "Saúde", icon: Heart },
  { name: "EcoShop", category: "E-commerce", icon: ShoppingCart },
  { name: "FinanceHub", category: "Fintech", icon: TrendingUp },
  { name: "EduTech", category: "Educação", icon: Users },
  { name: "FoodDelivery", category: "Delivery", icon: Zap },
  { name: "RealEstate", category: "Imóveis", icon: Building2 },
  { name: "SportsFit", category: "Fitness", icon: Heart },
  { name: "AutoCare", category: "Automotivo", icon: ShoppingCart },
  { name: "TravelApp", category: "Turismo", icon: TrendingUp },
  { name: "MusicStream", category: "Mídia", icon: Users },
  { name: "GameStudio", category: "Jogos", icon: Zap }
];

const testimonials = [
  {
    id: 1,
    name: "João Silva",
    position: "CEO",
    company: "TechCorp",
    content: "A AlphaLabs transformou nossa presença digital. O e-commerce desenvolvido aumentou nossas vendas em 120%.",
    rating: 5,
    avatar: "https://i.pravatar.cc/150?img=1"
  },
  {
    id: 2,
    name: "Maria Santos",
    position: "Diretora de TI",
    company: "InnovaGroup",
    content: "O sistema ERP personalizado otimizou nossos processos. Reduzimos 60% do tempo em tarefas administrativas.",
    rating: 5,
    avatar: "https://i.pravatar.cc/150?img=2"
  },
  {
    id: 3,
    name: "Carlos Oliveira",
    position: "Gerente",
    company: "StartupXYZ",
    content: "App mobile entregue no prazo e com qualidade excepcional. Nossa base de usuários cresceu 300%.",
    rating: 5,
    avatar: "https://i.pravatar.cc/150?img=3"
  },
  {
    id: 4,
    name: "Roberto Lima",
    position: "CMO",
    company: "DigitalFlow",
    content: "O site institucional desenvolvido é impressionante. SEO otimizado trouxe 85% mais leads qualificados.",
    rating: 5,
    avatar: "https://i.pravatar.cc/150?img=4"
  },
  {
    id: 5,
    name: "Juliana Ferreira",
    position: "CTO",
    company: "SecureNet",
    content: "Auditoria de segurança impecável. Identificaram vulnerabilidades que nem sabíamos que existiam.",
    rating: 5,
    avatar: "https://i.pravatar.cc/150?img=5"
  },
  {
    id: 6,
    name: "Oliver Mendes",
    position: "Fundadora",
    company: "EcoStore",
    content: "E-commerce desenvolvido superou expectativas. Interface intuitiva aumentou conversões em 90%.",
    rating: 5,
    avatar: "https://i.pravatar.cc/150?img=6"
  },
  {
    id: 7,
    name: "Ricardo Alves",
    position: "Diretor",
    company: "LogiTech",
    content: "Sistema de automação reduziu custos operacionais em 40%. ROI positivo em apenas 3 meses.",
    rating: 5,
    avatar: "https://i.pravatar.cc/150?img=7"
  },
  {
    id: 8,
    name: "Marcio Rocha",
    position: "CEO",
    company: "HealthPlus",
    content: "App de telemedicina desenvolvido com excelência. Atendimento 24h melhorou satisfação dos pacientes.",
    rating: 5,
    avatar: "https://i.pravatar.cc/150?img=8"
  },
  {
    id: 9,
    name: "Roberta Lucia",
    position: "Diretor Comercial",
    company: "VendaMais",
    content: "Automação de vendas desenvolvida revolucionou nosso processo. Aumento de 150% na produtividade da equipe.",
    rating: 5,
    avatar: "https://i.pravatar.cc/150?img=9"
  }
];

const successStories = [
  {
    client: "EcoShop",
    industry: "E-commerce",
    icon: ShoppingCart,
    challenge: "Precisavam de uma plataforma robusta para vender produtos sustentáveis online com integração de pagamentos e logística.",
    results: ["+300% vendas", "95% uptime", "Checkout otimizado"]
  },
  {
    client: "HealthPlus",
    industry: "Saúde Digital",
    icon: Heart,
    challenge: "Desenvolvimento de app para telemedicina com agendamentos, consultas online e prontuário eletrônico.",
    results: ["+50k usuários", "4.8★ rating", "Consultas 24/7"]
  },
  {
    client: "TechCorp",
    industry: "SaaS B2B",
    icon: Building2,
    challenge: "Plataforma SaaS complexa para gestão empresarial com múltiplos módulos e integrações com APIs externas.",
    results: ["500+ empresas", "99.9% uptime", "API robusta"]
  }
];

export default ClientsSection;